/*
 * PES Search — contractor synonym / expansion map (V12, Agent-15)
 * Presentation-only. Consumed by assets/pes-search-enhance.js.
 * Exposes window.PesSearchSyn with:
 *   .expand(term)   -> ordered array of alternative query strings
 *   .isSkuLike(term)-> true when the term looks like a SKU/MPN
 *   .families       -> part-number prefix -> brand/family map (for labels)
 */
(function () {
  'use strict';

  // Exact-term expansions (keys are lowercase). Values are tried in order.
  var EXACT = {
    // connectors
    'mc4': ['MC4 connector', 'solar connector'],
    'mc4 connectors': ['MC4 connector'],
    'mc4 connector': ['MC4'],
    'connectors': ['MC4 connector', 'solar connector'],
    // wire
    'thhn': ['THHN wire', 'THHN THWN copper'],
    'thhn wire': ['THHN'],
    'thwn': ['THHN wire'],
    'romex': ['NM-B wire', 'wire cable'],
    'pv wire': ['PV cable', 'solar PV wire'],
    'pv cable': ['PV wire', 'solar PV wire'],
    'use-2': ['PV wire', 'PV cable'],
    'use2': ['PV wire', 'PV cable'],
    'solar wire': ['PV wire', 'PV cable'],
    '10 awg pv': ['10 AWG PV wire', '10 AWG solar wire'],
    '10awg': ['10 AWG PV wire'],
    // inverters
    'micro': ['microinverter'],
    'micros': ['microinverter'],
    'micro inverter': ['microinverter'],
    'micro-inverter': ['microinverter'],
    'iq8': ['Enphase IQ8 microinverter', 'IQ8PLUS'],
    'iq8plus': ['Enphase IQ8+ microinverter', 'IQ8PLUS-72-2-US'],
    'iq8+': ['IQ8PLUS', 'Enphase IQ8+ microinverter'],
    'string inverter': ['inverter'],
    // panels
    'sil-430': ['Silfab 430', 'SIL-430-QD'],
    'sil430': ['SIL-430', 'Silfab 430'],
    'silfab 430': ['SIL-430'],
    'cs7n': ['Canadian Solar CS7N', 'Canadian Solar'],
    '400w panel': ['400W solar panel', 'solar panels'],
    // racking
    'xr100': ['IronRidge XR100', 'XR-100'],
    'xr-100': ['XR100', 'IronRidge XR100'],
    'xr10': ['IronRidge XR10', 'XR-10'],
    'xr1000': ['IronRidge XR1000', 'XR-1000'],
    'rail': ['XR100 rail', 'IronRidge rail'],
    'ironridge rail': ['XR100 rail'],
    // generators
    'generac 22kw': ['22kW Generac', 'Generac 7260'],
    '22kw': ['Generac 22kW', '22KW generator'],
    'generac 7260': ['Generac 22kW'],
    'standby generator': ['Generac generator', 'whole home generator'],
    // batteries
    'powerwall': ['Tesla Powerwall', 'battery storage'],
    'server rack battery': ['rack battery', 'LiFePO4 battery'],
    'lifepo4': ['LiFePO4 battery', 'lithium battery']
  };

  // Part-number prefix families (uppercase prefix -> brand / product family).
  var FAMILIES = [
    { prefix: 'SIL', brand: 'Silfab', family: 'solar panel', query: 'Silfab' },
    { prefix: 'IQ8', brand: 'Enphase', family: 'microinverter', query: 'Enphase IQ8' },
    { prefix: 'IQ7', brand: 'Enphase', family: 'microinverter', query: 'Enphase IQ7' },
    { prefix: 'CS7N', brand: 'Canadian Solar', family: 'solar panel', query: 'Canadian Solar' },
    { prefix: 'CS6', brand: 'Canadian Solar', family: 'solar panel', query: 'Canadian Solar' },
    { prefix: 'XR', brand: 'IronRidge', family: 'racking', query: 'IronRidge' },
    { prefix: 'Q.TRON', brand: 'Qcells', family: 'solar panel', query: 'Qcells' },
    { prefix: 'SE', brand: 'SolarEdge', family: 'inverter', query: 'SolarEdge' },
    { prefix: 'MNPV', brand: 'MidNite Solar', family: 'combiner', query: 'MidNite Solar' }
  ];

  var SKU_RE = /^(?=.*\d)[A-Za-z0-9][A-Za-z0-9\-_.\/]{2,24}$/;

  function isSkuLike(term) {
    var t = (term || '').trim();
    if (!t || t.indexOf(' ') !== -1) return false;
    return SKU_RE.test(t);
  }

  function familyFor(term) {
    var t = (term || '').trim().toUpperCase();
    for (var i = 0; i < FAMILIES.length; i++) {
      if (t.indexOf(FAMILIES[i].prefix) === 0) return FAMILIES[i];
    }
    return null;
  }

  // Ordered, de-duplicated expansion candidates (never includes the raw term).
  function expand(term) {
    var raw = (term || '').trim();
    if (!raw) return [];
    var lower = raw.toLowerCase();
    var out = [];
    function add(q) {
      q = (q || '').trim();
      if (q && q.toLowerCase() !== lower && out.indexOf(q) === -1) out.push(q);
    }

    // 1. exact map
    (EXACT[lower] || []).forEach(add);

    // 2. prefix family
    var fam = familyFor(raw);
    if (fam) {
      add(fam.query + ' ' + raw.replace(/[\-_]/g, ''));
      add(fam.query);
    }

    // 3. SKU-shape transforms
    if (isSkuLike(raw)) {
      add(raw.replace(/[\-_.]/g, ''));                    // SIL-430 -> SIL430
      var spaced = raw.replace(/([A-Za-z])(\d)/g, '$1 $2') // IQ8PLUS -> IQ8 PLUS
                      .replace(/(\d)([A-Za-z])/g, '$1 $2');
      add(spaced);
      add(spaced.replace(/[\-_.]/g, ' '));
    }

    // 4. multi-word: try singular/plural + hyphen join of first two words
    var words = lower.split(/\s+/);
    if (words.length > 1) {
      add(words.join('-'));
      if (words[words.length - 1].slice(-1) === 's') {
        add(words.slice(0, -1).concat(words[words.length - 1].slice(0, -1)).join(' '));
      }
    }

    return out.slice(0, 4);
  }

  window.PesSearchSyn = {
    EXACT: EXACT,
    families: FAMILIES,
    expand: expand,
    isSkuLike: isSkuLike,
    familyFor: familyFor
  };
})();
